import React, { useState } from "react";

import { useForm } from "react-hook-form";
import { PiQuestionBold } from "react-icons/pi";

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
  DrawerTrigger,
} from "@shared/components/ui/drawer";
import { Button } from "@shared/components/ui/Button";
import { useToaster } from "@shared/hooks/useToaster";
import { InputText } from "@shared/features/form/components/input-text";
import { InputTextarea } from "@shared/features/form/components/input-textarea";
import { Sentry } from "@shared/utils/init-tracking";
import { useUser } from "@shared/features/auth/hooks/useUser";

import { useSupport } from "../hooks/use-support";

interface SupportFormValues {
  subject: string;
  message: string;
}

export const SupportDrawer: React.FC = () => {
  const user = useUser();
  const { isOpen, close, open } = useSupport();
  const { toast } = useToaster();
  const {
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SupportFormValues>();

  const [isPending, setIsPending] = React.useState(false);

  const onSubmit = async (data: SupportFormValues) => {
    setIsPending(true);

    try {
      Sentry.captureFeedback({
        name: user?.common?.name || undefined,
        email: user?.common?.email || undefined,
        message: `[${data.subject}] ${data.message}`,
        url: window.location.href,
      });

      toast({
        title: "Votre demande a bien été envoyée !",
      });
      reset();
      close();
    } catch (error) {
      console.error("Failed to send feedback:", error);
      toast({
        title: "Une erreur est survenue lors de l'envoi de votre demande.",
      });
    } finally {
      setIsPending(false);
    }
  };

  const [isSupportDrawerDisplayed, setIsSupportDrawerDisplayed] =
    useState(true);

  // @ts-expect-error window not typed
  window.toggleSupport = () => {
    setIsSupportDrawerDisplayed((prev) => !prev);
  };

  // @ts-expect-error window not typed
  window.hideSupport = () => {
    setIsSupportDrawerDisplayed(() => false);
  };

  // @ts-expect-error window not typed
  window.showSupport = () => {
    setIsSupportDrawerDisplayed(() => true);
  };

  if (!isSupportDrawerDisplayed) {
    return null;
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && close()}>
      <DrawerTrigger asChild onClick={() => open()}>
        <Button className="fixed right-8 bottom-[12px] z-support rounded-full !size-[46px] text invisible md:visible">
          <PiQuestionBold className="size-[20px] shrink-0" />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle>Contactez le support</DrawerTitle>
            <DrawerDescription>
              Besoin d&apos;aide ? Envoyez-nous un message et nous vous
              répondrons dès que possible.
            </DrawerDescription>
          </DrawerHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="p-4 grid gap-4">
            <div className="grid gap-2">
              <InputText
                type="text"
                name="subject"
                options={{
                  placeholder: "Ex: Problème avec les rendez-vous",
                }}
                onChange={(value) => setValue("subject", value)}
                error={errors.subject?.message}
              />
            </div>

            <div className="grid gap-2">
              <InputTextarea
                type="textarea"
                name="message"
                placeholder="Décrivez votre problème en détail..."
                onChange={(value) => setValue("message", value)}
                error={errors.message?.message}
              />
            </div>

            <DrawerFooter className="px-0">
              <Button type="submit" disabled={isPending}>
                {isPending ? "Envoi en cours..." : "Envoyer"}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline">Annuler</Button>
              </DrawerClose>
            </DrawerFooter>
          </form>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
